import {
  trackMixpanelEvent,
  identifyMixpanelUser,
  setMixpanelUserProperties,
  resetMixpanel,
} from '@reapit-sales/mixpanel-provider'

/**
 * MixpanelProperties defines the properties that can be passed to Mixpanel tracking functions.
 * It includes the current path, destination path, screen, drawer, and models.
 * @interface MixpanelProperties
 * @property {string} [FromRoute] - The current route for tracking purposes.
 * @property {string} [Screen] - The screen name for tracking purposes.
 * @property {string} [Drawer] - The drawer name for tracking purposes.
 * @property {string} [Modal] - The modal name for tracking purposes.
 * @property {string} [Panel] - The panel name for tracking purposes.
 * @property {string} [DestinationRoute] - The destination route for navigation events.
 */
interface MixpanelProperties {
  Screen?: string
  Drawer?: string
  Modal?: string
  Panel?: string
  Menu?: string
  Script?: string
  FromRoute: string
  DestinationRoute?: string
}

/**
 * Tracks a general CRM user action.
 * This is your CRM app's specific wrapper around the generic Mixpanel tracking.
 */
export const trackMixpanelAction = (eventName: string, properties?: MixpanelProperties) => {
  trackMixpanelEvent(`Action_${eventName}`, properties)
}

/**
 * Tracks a general CRM user action.
 * This is your CRM app's specific wrapper around the generic Mixpanel tracking.
 */
export const trackMixpanelError = (eventName: string, properties?: Record<string, any>) => {
  trackMixpanelEvent(`${eventName}`, properties)
}

/**
 * Sets the user identity for CRM across all relevant tracking services.
 */
export const setMixpanelUserIdentity = (email?: string, properties?: Record<string, any>) => {
  if (email) {
    identifyMixpanelUser(email, { ...properties })
  }
}

/**
 * Updates properties for the current CRM user.
 */
export const updateMixpanelUserProperties = (properties: Record<string, any>) => {
  setMixpanelUserProperties(properties)
}

/**
 * Reports an error to CRM analytics and optionally to Sentry.
 * This function can be used to log errors that occur within the CRM application.
 *
 * Example error tracking
 * reportCrmError(error as Error, { api: 'fetchUserProfile' })
 */
export const reportMixpanelError = (error: Error, context?: Record<string, string>) => {
  // captureSentryException(error, context);
  trackMixpanelError('Error_Reported', { errorName: error.name, errorMessage: error.message, ...context })
}

/**
 * Resets the CRM user identity in analytics.
 * This is useful for logging out users or clearing session data.
 */
export const resetMixpanelAnalytics = () => {
  // Reset Mixpanel identity
  resetMixpanel()
}
